import PropTypes from 'prop-types';
import { useEnhancedQuery } from '../../hooks';
import {
  CUSTOMER,
  OPPORTUNITY,
} from '../../graphql';
import {
  clientCustomer,
  clientOpportunity,
} from '../..';

/**
 * Custom hook to check if the user has access to a customer
 * @param {string} customerId - The customer ID to check access for
 * @returns {boolean} - Whether the user has access
 */
function useCustomerAccess(customerId, shouldShowAll) {
  const { error } = useEnhancedQuery(CUSTOMER.READ(), {
    disableDefaultError: true,
    options: {
      client: clientCustomer,
      variables: {
        id: customerId,
        all: shouldShowAll,
      },
    },
  });
  if (error && error.message === 'GraphQL error: forbidden') {
    return false;
  }
  return true;
}

/**
 * Custom hook to fetch opportunities for a customer
 * @param {string} customerId - The customer ID to fetch opportunities for
 * @param {boolean} shouldShowAll - Whether to show all opportunities
 * @returns {Array} - Array of opportunities
 */
function useCustomerOpportunities(customerId, shouldShowAll) {
  const { data } = useEnhancedQuery(OPPORTUNITY.LIST(), {
    options: {
      client: clientOpportunity,
      variables: {
        customer_id: customerId,
        sorts: [{ field: 'created_at', order: 'dsc' }],
        limit: 50,
        per_query: 50,
        pit_id: '',
        search_after: '',
        // CPI-1197: Grant Vietnam Sales Admin L1-level access
        all: shouldShowAll,
      },
    },
    defaultData: {
      listOpportunities: {
        opportunities: [],
        count: 0,
      },
    },
    selector: (d) => d.listOpportunities,
  });

  return (data && data.opportunities) || [];
}

/**
 * Wrapper component that handles data fetching for a single customer row.
 * This component calls hooks at the top level, complying with the Rules of Hooks.
 */
export default function CustomerRow({ customer, shouldShowAll, renderRow }) {
  const haveAccess = useCustomerAccess(customer.id, shouldShowAll);
  const opportunities = useCustomerOpportunities(customer.id, shouldShowAll);

  const customerWithData = {
    ...(customer.contact || {}),
    id: customer.id,
    opportunities,
    haveAccess,
  };

  return renderRow(customerWithData);
}

CustomerRow.propTypes = {
  customer: PropTypes.shape({
    id: PropTypes.string.isRequired,
    contact: PropTypes.object,
  }).isRequired,
  shouldShowAll: PropTypes.bool.isRequired,
  renderRow: PropTypes.func.isRequired,
};
